// src/pages/registeredUsers/MyListings.tsx
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTags, faBoxOpen, faPlus, faEdit, faCheck, faTrashAlt, faMoneyBillWave } from '@fortawesome/free-solid-svg-icons';
import { CardGrid, ContentSection, SectionHeader, EmptyState, EmptyStateIcon, EmptyStateText, Button, ListingItem, CardHeader, ListingImage, StatusBadge, CardBody, ListingTitle, ListingMeta, MediaPreview, MediaGrid, ListingActions, LoadingMessage, ErrorMessage } from '../../pages/registeredUsers/styles/listings.style';
import { Listing } from '../../types/listing';
import { useNavigate } from 'react-router-dom';

interface MyListingsProps {
  listings: Listing[];
  loadingListings: boolean;
  listingsError: string | null;
  handleDeleteListing: (id: string, imageUrls: string[], videoUrls: string[]) => void;
  handleMarkAsSold: (id: string) => void;
  setActiveTab: (tab: 'create' | 'my-listings' | 'overview' | 'profile') => void;
  stallNumber?: string;
}

const MyListings: React.FC<MyListingsProps> = ({ listings, loadingListings, listingsError, handleDeleteListing, handleMarkAsSold, setActiveTab, stallNumber }) => {
  const navigate = useNavigate();

  const handleEditListing = (listingId: string) => {
    // Store stall number in localStorage for the edit page to access
    if (stallNumber) {
      localStorage.setItem('currentStallNumber', stallNumber);
    }
    navigate(`/dashboard/edit-listing/${listingId}${stallNumber ? `?stallNumber=${stallNumber}` : ''}`);
  };

  if (loadingListings) {
    return <LoadingMessage>Loading listings...</LoadingMessage>;
  }

  if (listingsError) {
    return <ErrorMessage>{listingsError}</ErrorMessage>;
  }

  if (listings.length === 0) {
    return (
      <EmptyState>
        <EmptyStateIcon>
          <FontAwesomeIcon icon={faBoxOpen} size="3x" />
        </EmptyStateIcon>
        <EmptyStateText>
          <h3>No Listings Yet</h3>
          <p>You haven't posted any listings yet. Click "Post New Ad" to create your first listing!</p>
          <Button onClick={() => setActiveTab('create')}>
            <FontAwesomeIcon icon={faPlus} /> Create Listing
          </Button>
        </EmptyStateText>
      </EmptyState>
    );
  }

  return (
    <ContentSection>
      <SectionHeader>
        <h2><FontAwesomeIcon icon={faTags} /> Your Active Ads</h2>
        <p>Manage your current listings and create new ones</p>
      </SectionHeader>
      
      <CardGrid>
        {listings.map((listing: Listing) => (
          <ListingItem key={listing.id}>
            <CardHeader>
              <ListingImage>
                {listing.mediaDetails && listing.mediaDetails.length > 0 ? (
                  listing.mediaDetails[0].type === 'image' ? (
                    <img src={listing.mediaDetails[0].url} alt={listing.title} />
                  ) : (
                    <video src={listing.mediaDetails[0].url} controls />
                  )
                ) : (
                  <img src={`https://placehold.co/300x200/cccccc/333333?text=No+Media`} alt="No Media" />
                )}
              </ListingImage>
              <StatusBadge sold={listing.sold}>
                {listing.sold ? 'Sold' : 'Available'}
              </StatusBadge>
            </CardHeader>
            <CardBody>
              <ListingTitle>
                {listing.title}
              </ListingTitle>
              <ListingMeta>
                <span><FontAwesomeIcon icon={faTags} /> {listing.category}</span>
                {listing.price && (
                  <span><FontAwesomeIcon icon={faMoneyBillWave} /> UGX {listing.price.toLocaleString()}</span>
                )}
              </ListingMeta>
              <p>{listing.description.substring(0, 100)}{listing.description.length > 100 ? '...' : ''}</p>
              
              {listing.mediaDetails && listing.mediaDetails.length > 1 && (
                <MediaPreview>
                  <small>More Media:</small>
                  <MediaGrid>
                    {listing.mediaDetails.slice(1, 4).map((media, idx) => (
                      <div key={idx}>
                        {media.type === 'image' ? (
                          <img src={media.url} alt={`Media ${idx + 2}`} />
                        ) : (
                          <video src={media.url} />
                        )}
                      </div>
                    ))}
                    {listing.mediaDetails.length > 4 && (
                      <div className="more-count">+{listing.mediaDetails.length - 4}</div>
                    )}
                  </MediaGrid>
                </MediaPreview>
              )}
              
              <ListingActions>
                <Button
                  variant="outline"
                  onClick={() => handleEditListing(listing.id)}
                  size="sm"
                >
                  <FontAwesomeIcon icon={faEdit} /> Edit
                </Button>
                {!listing.sold && (
                  <Button 
                    onClick={() => handleMarkAsSold(listing.id)}
                    size="sm"
                  >
                    <FontAwesomeIcon icon={faCheck} /> Mark Sold
                  </Button>
                )}
                <Button 
                  variant="danger" 
                  onClick={() => handleDeleteListing(
                    listing.id, 
                    listing.images || [], 
                    listing.videos || []
                  )}
                  size="sm"
                >
                  <FontAwesomeIcon icon={faTrashAlt} /> Delete
                </Button>
              </ListingActions>
            </CardBody>
          </ListingItem>
        ))}
      </CardGrid>
    </ContentSection>
  );
};

export default MyListings;